class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedMap = new Map();
    this.focusElement = this.input;
    this.resetButton.addEventListener('click', this.clear.bind(this));
    this.input.addEventListener('input', FoxTheme.utils.debounce(this.onChange.bind(this), 300));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.searchContent = this.querySelector('.search__content');
    this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';
    this.header = document.querySelector('header');
    this.toggleNavigationButton = document.querySelector('.toggle-navigation-button');

    this.boundHandleClickOutside = this.handleClickOutside.bind(this);
    document.addEventListener('click', this.boundHandleClickOutside);
    this.searchProductTypes?.addEventListener('change', this.handleProductTypeChange.bind(this));

    this.isHeaderSearch = this.closest('header') !== null;

    document.addEventListener('menu-drawer:open', () => {
      this.classList.remove('predictive-search-open');
      document.body.classList.remove('search-open');
    });

    this.states = {
      OPEN: 'predictive-search-open',
      LOADING: 'btn--loading',
      SEARCH_OPEN: 'search-open',
    };
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }
  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }

  get searchProductTypes() {
    return this.querySelector('#SearchProductTypes');
  }

  async getShopifyEntityById(kind, id) {
    const cfg = {
    domain: 'shophomemed.myshopify.com',
    token: 'b8fabaa8ce534e74ed97282123b168b9',
    apiVersion: '2025-07',
    };

    const typeMap = {
      product: 'Product',
      page: 'Page',
      collection: 'Collection',
      article: 'Article',
      blog: 'Blog',
    };
    const expectedType = typeMap[kind];
    if (!expectedType) throw new Error(`Unknown kind "${kind}". Use "product" | "page" | "collection" | "article" | "blog".`);

    const gid = String(id).startsWith('gid://') ? String(id) : `gid://shopify/${expectedType}/${id}`;

    const query = `
      query getNode($id: ID!) {
        node(id: $id) {
          __typename

          ... on Product {
            handle,
            title
          }

          ... on Page {
            handle,
            title
          }

          ... on Collection {
            handle,
            title
          }

          ... on Article {
            handle,
            title
          }

          ... on Blog {
            handle,
            title
          }
        }
      }
    `;

    const res = await fetch(`https://${cfg.domain}/api/${cfg.apiVersion}/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': cfg.token,
      },
      body: JSON.stringify({ query, variables: { id: gid } }),
    });

    if (!res.ok) {
      const txt = await res.text();
      throw new Error(`Storefront API error ${res.status}: ${txt}`);
    }

    const { data, errors } = await res.json();
    if (errors) throw new Error(errors.map(e => e.message).join('; '));

    const node = data?.node;
    if (!node) return null;

    if (node.__typename !== expectedType) {
      throw new Error(`ID resolves to ${node.__typename}, not ${expectedType}.`);
    }

    // ---- Normalized outputs per type ----
    if (node.__typename === 'Product') {
      return {
        handle: node.handle,
        title: node.title,
      };
    }

    if (node.__typename === 'Page') {
      return {
        handle: node.handle,
        title: node.title,
      };
    }

    if (node.__typename === 'Collection') {
      return {
        handle: node.handle,
        title: node.title,
        
      };
    }

    if (node.__typename === 'Article') {
      const blogHandle = node.blog?.handle ?? 'news';
      return {
        handle: node.handle,
        title: node.title,
      
      };
    }

    // Blog
    return {
        handle: node.handle,
        title: node.title,
    };
  }

  onFocus(event) {
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom)}px`
    );
    if (this.isHeaderSearch && !this.searchRecommendationEmpty) {
      document.body.classList.add('search-open');
      this.toggleNavButtonVisibility(false);
    }

    if (!this.searchRecommendationEmpty) {
      this.searchContent.classList.remove('hidden');
    }
    this.classList.add('predictive-search-open');
    if (this.getQuery().length === 0) {
      if (this.searchRecommendationEmpty) {
        this.searchContent.classList.add('hidden');
      }
      return;
    }
    this.setupURL().then((data) => {
      const url = data.toString();
      this.renderSection(url);

    });
  }

  getQuery() {
    return this.input.value.trim();
  }

  clear(event = null) {
    event?.preventDefault();
    this.input.value = '';
    this.input.focus();
    this.removeAttribute('results');
    this.toggleSearchState(false);
  }

  handleProductTypeChange(evt) {
    const query = this.getQuery();
    if (query.length > 0) {
      this.setupURL().then((data) => {
        const url = data.toString();
        this.renderSection(url);
      });
    }
  }

  setupURL() {
      const url = new URL(`${FoxTheme.routes.shop_url}${FoxTheme.routes.predictive_search_url}`);
  let searchTerm = this.getQuery();

  if (this.searchProductTypes && this.searchProductTypes.value) {
    searchTerm = `product_type:${this.searchProductTypes.value} AND ${searchTerm}`;
  }

  const finalize = () => {
    url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 3);
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this));

    if (!url.searchParams.get('resources[options][fields]')) {
      url.searchParams.set('resources[options][fields]', 'title,variants.sku');
    }

    return url;
  };

  if (searchTerm.includes('REF-')) {
    const [_, refType, id] = searchTerm.split('-');
    if (refType === '1') {
      return this.getShopifyEntityById('product', id)
        .then(product => {
          if(product !== null) {
            url.searchParams.set('resources[type]', 'product');
            url.searchParams.set('resources[options][fields]', 'title'); // force only title
            url.searchParams.set('q', product.title);
          }
        })
        .then(finalize); // return the URL after setting common params
    } else if(refType == '2') {
      return this.getShopifyEntityById('collection', id)
        .then(collection => {
          if(collection !== null) {
            url.searchParams.set('resources[type]', 'collection');
            url.searchParams.set('resources[options][fields]', 'title'); // force only title
            url.searchParams.set('q', collection.title);
          }
        })
        .then(finalize); // return the URL after setting common params
    } else if(refType == '3') {
      return this.getShopifyEntityById('page', id)
        .then(page => {
          if(page !== null) {
            url.searchParams.set('resources[type]', 'page');
            url.searchParams.set('resources[options][fields]', 'title'); // force only title
            url.searchParams.set('q', page.title);
          }
        })
        .then(finalize); // return the URL after setting common params
    } else if(refType == '4') {
      return this.getShopifyEntityById('blog', id)
        .then(blog => {
          if(blog !== null){
            // url.searchParams.set('resources[type]', 'query');
            // url.searchParams.set('resources[options][fields]', 'title'); // force only title
            url.searchParams.set('q', blog.title);
          }
        })
        .then(finalize); // return the URL after setting common params
    } else if(refType == '5') {
      return this.getShopifyEntityById('article', id)
        .then(article => {
          if(article !== null){
            url.searchParams.set('resources[type]', 'article');
            url.searchParams.set('resources[options][fields]', 'title'); // force only title
            url.searchParams.set('q', article.title);
          }
        })
        .then(finalize); // return the URL after setting common params
    }

    // default fallback if REF-* not handled:
    return Promise.resolve().then(finalize);
  } else {
    url.searchParams.set('q', searchTerm);
    return Promise.resolve(finalize());
  }
  }

  onChange() {
    if (this.getQuery().length === 0) {
      this.clear();
      return;
    }
      this.setupURL().then((data) => {
        const url = data.toString();
        this.renderSection(url);
      });
  }

  renderSection(url) {
    this.cachedMap.has(url) ? this.renderSectionFromCache(url) : this.renderSectionFromFetch(url);
  }

  renderSectionFromCache(url) {
    const responseText = this.cachedMap.get(url);
    this.renderSearchResults(responseText), this.setAttribute('results', '');
  }

  renderSectionFromFetch(url) {
    this.setLoadingState(true);

    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.text();
      })
      .then((responseText) => {
        this.renderSearchResults(responseText);
        this.cachedMap.set(url, responseText);
      })
      .catch((error) => {
        console.error('Error fetching data: ', error);
        this.setAttribute('error', 'Failed to load data');
      })
      .finally(() => this.setLoadingState(false));
  }
  renderSearchResults(responseText) {
    const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
    const targetElement = document.getElementById(id);

    if (targetElement) {
      const parser = new DOMParser();
      const parsedDoc = parser.parseFromString(responseText, 'text/html');
      const contentElement = parsedDoc.getElementById(id);

      if (contentElement) {
        this.searchContent?.classList.remove('hidden');
        targetElement.innerHTML = contentElement.innerHTML;

        if (this.isHeaderSearch && !document.body.classList.contains('search-open')) {
          document.body.classList.add('search-open');
          this.toggleNavButtonVisibility(true);
          this.classList.add('predictive-search-open');
        } else {
          this.classList.add('predictive-search-open');
        }
      } else {
        console.error(`Element with id '${id}' not found in the parsed response.`);
      }
    } else {
      console.error(`Element with id '${id}' not found in the document.`);
    }
  }

  handleClickOutside(event) {
    const target = event.target;
    const shouldClose = this.isHeaderSearch
      ? !this.contains(target) &&
        ((target.classList.contains('fixed-overlay') && target.closest('.header-section')) ||
          target.classList.contains('header__search-close'))
      : !this.contains(target);

    if (shouldClose) {
      setTimeout(() => this.toggleSearchState(false));
    }
  }

  toggleSearchState(isOpen) {
    this.classList.toggle(this.states.OPEN, isOpen);
    if (this.isHeaderSearch) {
      document.body.classList.toggle(this.states.SEARCH_OPEN, isOpen);
      this.toggleNavButtonVisibility(!isOpen);
    }
    if (!isOpen && this.searchRecommendationEmpty) {
      this.searchContent?.classList.add('hidden');
    }
  }

  toggleNavButtonVisibility(isOpen) {
    this.toggleNavigationButton && this.toggleNavigationButton.classList.toggle('is-show', isOpen);
  }

  setLoadingState(isLoading) {
    if (isLoading) {
      this.setAttribute('loading', 'true');
      this.resetButton.classList.add(this.states.LOADING);
    } else {
      this.removeAttribute('loading');
      this.resetButton.classList.remove(this.states.LOADING);
      this.setAttribute('results', 'true');
    }
  }
}
customElements.define('predictive-search', PredictiveSearch);
